import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SocialProofToast } from './SocialProofToast';
import { trackEvent, buildCheckoutUrl } from '../utils/pixel';
import { Check, ShieldCheck, ChevronDown, Lock, ArrowRight, Star, Zap, Hand } from 'lucide-react';
interface OfferScreenProps {
  userName: string;
}
export const OfferScreen: React.FC<OfferScreenProps> = ({
  userName
}) => {
  const [bump1, setBump1] = useState(false);
  const [bump2, setBump2] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const basePrice = 359;
  const bump1Price = 178;
  const bump2Price = 117;
  const totalPrice = basePrice + (bump1 ? bump1Price : 0) + (bump2 ? bump2Price : 0);
  // Savings grow as the user adds order bumps (each bump has an alleged retail
  // value higher than what we charge). Start low, increase with each addition.
  const baseSavings = 242;
  const bump1ExtraSavings = 172; // alleged retail $350 vs $178 charged
  const bump2ExtraSavings = 163; // alleged retail $280 vs $117 charged
  const totalSavings = baseSavings + (bump1 ? bump1ExtraSavings : 0) + (bump2 ? bump2ExtraSavings : 0);
  const getCheckoutUrl = () => {
    if (bump1 && bump2) return 'https://pay.hotmart.com/V105752329L?off=d5xw6fls';
    if (bump1) return 'https://pay.hotmart.com/V105752329L?off=3j0eysn0';
    if (bump2) return 'https://pay.hotmart.com/V105752329L?off=8z0c7eyt';
    return 'https://pay.hotmart.com/V105752329L?off=rxli9jm6';
  };
  const handleCheckout = () => {
    const baseUrl = getCheckoutUrl();
    let finalUrl = baseUrl;
    try {
      // Fire InitiateCheckout BEFORE redirect so the Pixel has time to flush
      trackEvent('InitiateCheckout', {
        value: totalPrice,
        currency: 'MXN',
        content_name: 'Protocolo de 7 Días para Reiniciar tus Hormonas',
        content_ids: ['protocolo-7-dias'],
        content_type: 'product',
        num_items: 1 + (bump1 ? 1 : 0) + (bump2 ? 1 : 0),
        bump1_added: bump1,
        bump2_added: bump2
      });
      // Enhance the Hotmart URL with full attribution tracking (UTMs, sck/src, fbp, fbc, fbclid)
      finalUrl = buildCheckoutUrl(baseUrl);
    } catch (err) {
      // Tracking must never block the redirect — fall back to the base URL
      console.warn('[checkout] tracking enhancement failed', err);
      finalUrl = baseUrl;
    }
    // Tiny delay so the Pixel beacon has time to dispatch before navigation
    window.setTimeout(() => {
      window.location.href = finalUrl;
    }, 120);
  };
  // Fire ViewContent and track bump toggles for the Meta Pixel
  useEffect(() => {
    trackEvent('ViewContent', {
      content_name: 'Offer Page',
      value: basePrice,
      currency: 'MXN'
    });
  }, []);
  useEffect(() => {
    if (bump1) {
      trackEvent('AddToCart', {
        content_name: 'Order Bump 1: Checklist Hogar Libre',
        value: bump1Price,
        currency: 'MXN'
      });
    }
  }, [bump1]);
  useEffect(() => {
    if (bump2) {
      trackEvent('AddToCart', {
        content_name: 'Order Bump 2: Drenaje Linfático Facial',
        value: bump2Price,
        currency: 'MXN'
      });
    }
  }, [bump2]);
  const scrollToOffer = () => {
    document.getElementById('todo-recibes-hoy')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  const fadeUp = {
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };
  return <div className="min-h-screen bg-cream font-sans text-dark-text" data-id="element-38">
      {/* A. Top Launch Banner */}
      <div className="w-full bg-[#B45309] text-white text-center py-2.5 px-4" data-id="element-39">
        <p className="text-xs md:text-sm font-bold flex items-center justify-center gap-1 flex-wrap" data-id="element-40">
          <Zap className="w-4 h-4 fill-current" data-id="element-41" /> Precio de lanzamiento: 40%
          menos. Al terminar esta etapa, sube a $601 MXN.
        </p>
      </div>

      {/* B. Hero Section */}
      <section className="py-12 md:py-16 px-4 max-w-4xl mx-auto text-center" data-id="element-42">
        <p className="text-xs md:text-sm font-bold tracking-[0.2em] text-forest-green uppercase mb-6" data-id="element-43">
          PARA MUJERES 40+ QUE YA PROBARON TODO
        </p>
        <motion.h1 initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeUp} className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.1] mb-6 text-dark-text" data-id="element-44">
          {userName}, Te revelo el protocolo de 7 días para reiniciar tus
          hormonas sin terapia, sin suplementos y sin salir de tu casa
        </motion.h1>
        <motion.p initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeUp} className="text-[1.1rem] md:text-xl text-dark-text/80 mb-8 max-w-3xl mx-auto leading-relaxed" data-id="element-45">
          Hay una razón por la que nada ha funcionado hasta ahora. Y no es otro
          suplemento. Es una señal que tu cuerpo dejó de recibir — y que este
          protocolo reactiva usando tu propia temperatura.
        </motion.p>

        <p className="text-sm font-semibold text-forest-green mb-8 uppercase tracking-wider" data-id="element-46">
          Guía Digital • Descarga Inmediata • Empiezas Esta Noche
        </p>

        {/* Product Bundle Mockup */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeUp} className="max-w-3xl mx-auto mb-10" data-id="element-47">
          <img src="https://cdn.magicpatterns.com/uploads/g8ikyFtW1htJZpar1d4KEN/1.-Bundle-procolo-7-dias-menopausia-_all-mockups_-_1_.webp" alt="Bundle Completo - Protocolo de 7 Días + 4 Bonos" className="w-full h-auto rounded-2xl" data-id="element-48" />
        </motion.div>

        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeUp} data-id="element-49">
          <button onClick={scrollToOffer} className="w-full max-w-md mx-auto bg-forest-green text-white py-5 px-6 rounded-xl font-bold text-lg md:text-xl flex items-center justify-center gap-2 hover:bg-[#0f4430] transition-colors shadow-[0_8px_25px_rgba(20,88,62,0.3)] mb-2" data-id="element-50">
            Obtener El Protocolo — $359 MXN <ArrowRight className="w-5 h-5" data-id="element-51" />
          </button>
          <p className="text-xs text-muted-text mb-6" data-id="element-52">(IVA incluido)</p>
          <p className="text-xs text-muted-text italic mb-3" data-id="element-53">
            Desliza para ver todo lo que incluye
          </p>
          <div className="flex items-center justify-center gap-2 text-sm font-medium text-forest-green" data-id="element-54">
            <ShieldCheck className="w-4 h-4" data-id="element-55" /> Garantía de 7 Noches •
            Devolución Total
          </div>
        </motion.div>
      </section>

      {/* C. No te quedaste sin hormonas */}
      <section className="py-16 bg-[#F4EFE6] px-4" data-id="element-56">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeUp} className="max-w-3xl mx-auto" data-id="element-57">
          <h2 className="font-serif text-3xl md:text-4xl font-bold mb-8 text-dark-text" data-id="element-58">
            No te "quedaste sin hormonas". La señal simplemente dejó de llegar.
          </h2>
          <div className="space-y-6 text-[1.1rem] text-dark-text/85 leading-relaxed" data-id="element-59">
            <p data-id="element-60">
              Te despiertas a las 3 AM empapada en sudor. Se te olvida tu propio
              número de teléfono en el banco. Le gritas a tu esposo por nada y
              terminas llorando en el baño sin saber por qué.
            </p>
            <p data-id="element-61">
              Probaste suplementos. Dieta keto. Ayuno intermitente. Meditación.
              Te sentaste en consultorios médicos con listas de síntomas
              mientras te ofrecían otra receta que ya sabías que no iba a
              funcionar.
            </p>
            <p data-id="element-62">
              El problema nunca fue lo que le estabas metiendo a tu cuerpo. El
              problema es que la <strong data-id="element-63">señal</strong> entre tu cerebro y tus
              ovarios se interrumpió. Y sin esa señal, ningún suplemento del
              mundo va a funcionar.
            </p>
            <p className="text-xl font-serif italic text-forest-green border-l-4 border-forest-green pl-6 py-2" data-id="element-64">
              Todos los suplementos del mundo no van a arreglar una señal rota.
              Pero la señal correcta — entregada en el momento correcto — puede
              cambiarlo todo en 7 días.
            </p>
          </div>
        </motion.div>
      </section>

      {/* E. Método de Cambio Térmico */}
      <section className="py-16 bg-white px-4" data-id="element-65">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeUp} className="max-w-3xl mx-auto" data-id="element-66">
          <h2 className="font-serif text-3xl md:text-4xl font-bold mb-4 text-center" data-id="element-67">
            Conoce El Método de Cambio Térmico™
          </h2>
          <p className="text-lg text-center text-forest-green italic mb-10 max-w-2xl mx-auto font-medium" data-id="element-68">
            No se trata de lo que le METES a tu cuerpo. Se trata de la SEÑAL que
            le envías.
          </p>

          <div className="space-y-6 text-[1.1rem] text-dark-text/85 leading-relaxed mb-10" data-id="element-69">
            <p data-id="element-70">
              Cuando empieza la perimenopausia, tu eje HPA — el panel de control
              maestro que conecta tu cerebro con tus ovarios — empieza a recibir
              una 'señal de apagado'. Una por una, las compuertas térmicas se
              cierran.
            </p>
            <p data-id="element-71">
              Tu cuerpo todavía tiene la capacidad de producir lo que necesita.
              Pero la señal no llega. Y todos los suplementos del mundo no van a
              arreglar una señal rota.
            </p>
            <p data-id="element-72">
              El Método de Cambio Térmico™ usa la señal más primitiva que tu
              cuerpo reconoce — la temperatura — para reabrir esas compuertas.
              Sin pastillas. Sin terapias hormonales. Solo el estímulo correcto
              en el momento correcto.
            </p>
          </div>

          {/* Diagram Image */}
          <div className="bg-[#F9F7F2] rounded-3xl p-4 md:p-6 border border-[#E8E2D5]" data-id="element-73">
            <img src="https://cdn.magicpatterns.com/uploads/fAwibA2HpRWU9FNxUUYPPj/ChatGPT_Image_11_may_2026,_20_14_53_(1).webp" alt="Antes y Después: Cómo el Cambio Térmico reabre la compuerta entre tu eje HPA y tus ovarios" className="w-full rounded-2xl" data-id="element-74" />
            <p className="text-center text-sm text-dark-text/70 italic mt-5 px-2" data-id="element-75">
              El eje HPA envía la señal. Una compuerta térmica cerrada la
              bloquea. El Método de Cambio Térmico™ la reabre.
            </p>
          </div>
        </motion.div>
      </section>

      {/* F. Imagina despertar */}
      <section className="py-16 bg-cream px-4" data-id="element-76">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeUp} className="max-w-3xl mx-auto" data-id="element-77">
          <h2 className="font-serif text-3xl md:text-4xl font-bold mb-10 text-center" data-id="element-78">
            Imagina despertar en 7 días y sentir esto:
          </h2>
          <div className="space-y-4" data-id="element-79">
            {[{
            title: 'Cuando apagas la luz',
            desc: 'Te duermes en 10 minutos y no te vuelven a despertar los bochornos. Duermes 7 horas de corrido por primera vez en años.'
          }, {
            title: 'Cuando abres los ojos',
            desc: 'Te levantas con la energía que creías perdida. Sin niebla mental. Tu cabeza responde como antes — ágil, clara, tuya.'
          }, {
            title: 'Cuando tu esposo te habla',
            desc: 'No explotas. No lloras sin razón. Le respondes con la paciencia que tenías hace 5 años. Y él lo nota.'
          }, {
            title: 'Cuando te miras al espejo',
            desc: 'Reconoces a la mujer que ves. No es la versión agotada de los últimos años. Eres tú otra vez. La de antes. La que creías perdida.'
          }].map((item, i) => <div key={i} className="bg-white p-6 rounded-2xl border border-[#E8E2D5] flex gap-5 items-start shadow-sm" data-id="element-80">
                <div className="bg-forest-green text-white w-10 h-10 rounded-full flex items-center justify-center shrink-0 font-bold" data-id="element-81">
                  {i + 1}
                </div>
                <div data-id="element-82">
                  <h4 className="font-serif font-bold text-xl text-dark-text mb-2" data-id="element-83">
                    {item.title}
                  </h4>
                  <p className="text-dark-text/80 leading-relaxed" data-id="element-84">
                    {item.desc}
                  </p>
                </div>
              </div>)}
          </div>

          <div className="mt-10 bg-mint-green/30 border border-mint-green rounded-2xl p-6 text-center text-base text-forest-green font-medium italic leading-relaxed" data-id="element-85">
            No es magia. Es biología. Cuando la señal térmica correcta llega a
            tus ovarios, tu cuerpo recuerda exactamente qué hacer. Solo
            necesitaba que alguien le hablara en el idioma correcto.
          </div>
        </motion.div>
      </section>

      {/* G. Dentro del Protocolo */}
      <section className="py-16 bg-white px-4" data-id="element-86">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeUp} className="max-w-3xl mx-auto" data-id="element-87">
          <h2 className="font-serif text-3xl md:text-4xl font-bold mb-4 text-center" data-id="element-88">
            Dentro del Protocolo de 7 Días
          </h2>
          <p className="text-center text-muted-text mb-12 italic" data-id="element-89">
            Tres fases. Siete días. Una señal que tu cuerpo ya entiende.
          </p>

          <div className="space-y-6" data-id="element-90">
            {[{
            pill: 'Días 1–2',
            pillColor: 'bg-[#E0626B] text-white',
            title: 'El Protocolo de Enfriamiento',
            desc: "La rutina matutina de 7 minutos que baja tu temperatura basal. El ritual nocturno que detuvo los bochornos en 48 horas. La 'Ventana Térmica' de 20 minutos que se abre una vez al día — y cómo activarla."
          }, {
            pill: 'Días 3–5',
            pillColor: 'bg-[#D97706] text-white',
            title: 'El Reinicio Térmico',
            desc: "El protocolo de ducha de 4 minutos que suena absurdo pero funciona. Exactamente qué comer a qué hora exacta — aquí la cronobiología supera cualquier dieta. La 'Ventana Térmica' de 20 minutos que se abre una vez al día."
          }, {
            pill: 'Días 6–7',
            pillColor: 'bg-forest-green text-white',
            title: 'El Anclaje',
            desc: '3 biomarcadores para confirmar que tu reinicio funcionó (sin análisis de sangre). El protocolo de mantenimiento de 1 minuto al día. Por qué a veces los síntomas regresan — y la solución simple.'
          }].map((item, i) => <div key={i} className="bg-cream p-8 rounded-3xl border-l-4 border-mint-green shadow-sm" data-id="element-91">
                <span className={`inline-block ${item.pillColor} text-xs font-bold uppercase tracking-wider py-1.5 px-4 rounded-full mb-4`} data-id="element-92">
                  {item.pill}
                </span>
                <h4 className="font-serif font-bold text-2xl mb-3 text-dark-text" data-id="element-93">
                  {item.title}
                </h4>
                <p className="text-dark-text/80 text-lg leading-relaxed" data-id="element-94">
                  {item.desc}
                </p>
              </div>)}
          </div>

          <p className="text-center text-base font-serif italic text-forest-green mt-10 max-w-xl mx-auto" data-id="element-95">
            Al día 7, tus compuertas térmicas se reabren — y tu cuerpo recuerda
            qué hacer.
          </p>
        </motion.div>
      </section>

      {/* H. Quién creó este protocolo */}
      <section className="py-16 bg-cream px-4" data-id="element-96">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeUp} className="max-w-3xl mx-auto text-center" data-id="element-97">
          <h2 className="font-serif text-3xl md:text-4xl font-bold mb-10" data-id="element-98">
            ¿Quién creó este protocolo?
          </h2>

          <img src="https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=200&h=200&fit=crop&q=80" alt="Dra. Elena Morales" className="w-28 h-28 rounded-full mx-auto object-cover border-4 border-white shadow-lg mb-5" data-id="element-99" />
          <h3 className="text-2xl font-serif font-bold text-dark-text mb-2" data-id="element-100">
            Dra. Elena Morales
          </h3>
          <p className="text-sm text-muted-text mb-8" data-id="element-101">
            Especialista en Salud Hormonal Femenina · 18 años de experiencia
          </p>

          <div className="space-y-5 text-[1.1rem] text-dark-text/85 leading-relaxed text-left max-w-2xl mx-auto" data-id="element-102">
            <p data-id="element-103">
              Trabajé 18 años con pacientes en perimenopausia y menopausia.
              Receté terapias hormonales, suplementos, dietas. Vi a cientos de
              mujeres mejorar — y a otras cientos que no.
            </p>
            <p data-id="element-104">
              Las que no mejoraban no estaban "rotas". La señal no estaba
              llegando. Ahí fue cuando encontré la investigación sobre
              termorregulación circadiana y el eje HPA. Lo que siguió fueron 3
              años de prueba, ajuste y validación con pacientes reales.
            </p>
            <p className="font-serif italic text-forest-green text-xl" data-id="element-105">
              El Método de Cambio Térmico™ es el resultado. 7 días. Una ducha.
              Tu cuerpo.
            </p>
          </div>
        </motion.div>
      </section>

      {/* I. Cuánto cuesta seguir como estás */}
      <section className="py-16 bg-white px-4" data-id="element-106">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeUp} className="max-w-3xl mx-auto" data-id="element-107">
          <h2 className="font-serif text-3xl md:text-4xl font-bold mb-4 text-center" data-id="element-108">
            ¿Cuánto cuesta seguir como estás?
          </h2>
          <p className="text-center text-muted-text mb-10 max-w-2xl mx-auto" data-id="element-109">
            Esto es lo que las mujeres están gastando para intentar arreglar lo
            que una señal rota causa:
          </p>

          <div className="grid md:grid-cols-2 gap-6" data-id="element-110">
            {/* Left: Alternatives */}
            <div className="bg-red-50/40 rounded-3xl p-6 border border-red-100" data-id="element-111">
              <p className="text-xs font-bold uppercase tracking-widest text-red-600 mb-5 text-center" data-id="element-112">
                Lo que YA estás gastando (o gastarías)
              </p>
              <div className="space-y-4" data-id="element-113">
                <div className="flex justify-between items-start gap-3 pb-3 border-b border-red-100/70" data-id="element-114">
                  <span className="text-dark-text/85 text-sm" data-id="element-115">
                    Suplementos mensuales
                  </span>
                  <span className="text-red-600 font-bold text-sm whitespace-nowrap" data-id="element-116">
                    $1,200–$2,400/mes
                  </span>
                </div>
                <div className="flex justify-between items-start gap-3 pb-3 border-b border-red-100/70" data-id="element-117">
                  <span className="text-dark-text/85 text-sm" data-id="element-118">
                    Consulta + terapia hormonal
                  </span>
                  <span className="text-red-600 font-bold text-sm whitespace-nowrap" data-id="element-119">
                    $4,000–$10,000/año
                  </span>
                </div>
                <div className="flex justify-between items-start gap-3 pb-3 border-b border-red-100/70" data-id="element-120">
                  <span className="text-dark-text/85 text-sm" data-id="element-121">
                    Apps de yoga/meditación
                  </span>
                  <span className="text-red-600 font-bold text-sm whitespace-nowrap" data-id="element-122">
                    $300–$600/mes
                  </span>
                </div>
                <div className="bg-red-600 text-white rounded-xl p-4 mt-4 flex justify-between items-center" data-id="element-123">
                  <span className="font-bold text-sm" data-id="element-124">
                    💸 Total mensual en alternativas
                  </span>
                  <span className="font-bold text-sm whitespace-nowrap" data-id="element-125">
                    $1,500–3,000/mes
                  </span>
                </div>
              </div>
            </div>

            {/* Right: This Method */}
            <div className="bg-mint-green/30 rounded-3xl p-6 border-2 border-forest-green" data-id="element-126">
              <p className="text-xs font-bold uppercase tracking-widest text-forest-green mb-5 text-center" data-id="element-127">
                Con El Método de Cambio Térmico™
              </p>
              <div className="flex flex-col h-[calc(100%-2rem)]" data-id="element-128">
                <div className="flex items-start gap-3 mb-4" data-id="element-129">
                  <Check className="w-5 h-5 text-forest-green shrink-0 mt-0.5" data-id="element-130" />
                  <span className="text-dark-text font-semibold" data-id="element-131">
                    Protocolo de 7 Días (este método)
                  </span>
                </div>
                <p className="text-sm text-dark-text/70 mb-6 pl-8" data-id="element-132">
                  Sin recetas. Sin suplementos. Garantía de 7 noches.
                </p>
                <div className="bg-white rounded-xl p-5 text-center mt-auto border-2 border-forest-green" data-id="element-133">
                  <p className="text-3xl font-black text-forest-green" data-id="element-134">
                    $359{' '}
                    <span className="text-base font-bold" data-id="element-135">una sola vez</span>
                  </p>
                  <p className="text-xs text-muted-text mt-1" data-id="element-136">(IVA incluido)</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* J & K. Todo Lo Que Recibes + Price Reveal */}
      <section id="todo-recibes-hoy" className="py-16 bg-[#F4EFE6] px-4 scroll-mt-4" data-id="element-137">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeUp} className="max-w-4xl mx-auto" data-id="element-138">
          <h2 className="font-serif text-4xl md:text-5xl font-bold mb-3 text-center text-dark-text" data-id="element-139">
            Todo Lo Que Recibes Hoy
          </h2>
          <p className="text-center text-muted-text mb-10" data-id="element-140">
            El sistema completo — protocolo + 4 bonos.
          </p>

          <div className="bg-white rounded-[2.5rem] p-6 md:p-10 shadow-xl border border-[#E8E2D5] mb-10" data-id="element-141">
            <img src="https://cdn.magicpatterns.com/uploads/g8ikyFtW1htJZpar1d4KEN/1.-Bundle-procolo-7-dias-menopausia-_all-mockups_-_1_.webp" alt="Sistema Completo - Bundle" className="w-full rounded-2xl mb-8 object-contain" data-id="element-142" />
            {/* Value Stack List */}
            <div className="space-y-4" data-id="element-143">
              {[{
              thumb: 'https://cdn.magicpatterns.com/uploads/mYMicpYSgxkBKp8CS5YXhh/ChatGPT-Image-11-may-2026_-15_46_56.webp',
              title: 'El Protocolo de 7 Días para Reiniciar tus Hormonas',
              value: '$1,449 MXN',
              desc: 'El protocolo completo de 7 días. La rutina matutina, el ritual nocturno, la Ventana Térmica y los 3 biomarcadores para confirmar tu reinicio. PDF digital • Descarga inmediata.'
            }, {
              thumb: 'https://cdn.magicpatterns.com/uploads/nfZCS1PKq1rgcauzFBpwJs/BONO-1-Los-5-Saboteadores-de-tu-Reinicio-Hormonal.webp',
              title: 'BONO 1: Los 5 Saboteadores de tu Reinicio Hormonal',
              value: '$359 MXN',
              desc: '5 alimentos cotidianos que bloquean tu señal térmica sin que lo sepas. El #3 está en tu cocina AHORA MISMO — eliminarlo puede ser la diferencia entre un reinicio completo y uno a medias.'
            }, {
              thumb: 'https://cdn.magicpatterns.com/uploads/5TxUxsxQ4Wk5s2AYjqR8Lp/BONO-2-Diario-de-Cambio-Termico-de-7-Dias.webp',
              title: 'BONO 2: Diario de Cambio Térmico de 7 Días',
              value: '$238 MXN',
              desc: 'Para que registres cómo se siente tu cuerpo cada día. La mayoría de las mujeres notan el PRIMER cambio en 48 horas. Aquí lo documentas.'
            }, {
              thumb: 'https://cdn.magicpatterns.com/uploads/qnaNf534jQpxB7UWfxtduj/BONO-3-Plan-de-Duchas-Termicas-Avanzado.webp',
              title: 'BONO 3: Plan de Duchas Térmicas Avanzado',
              value: '$299 MXN',
              desc: 'Cuando termines los 7 días, este plan te lleva al siguiente nivel. Mantenimiento de por vida para que nunca vuelvas a perder la señal.'
            }, {
              thumb: 'https://cdn.magicpatterns.com/uploads/nz6yLjCu6jER6PgUr4gCTx/BONO-4-Recetario-Anti-Inflamatorio-40-.webp',
              title: 'BONO 4: Recetario Anti-Inflamatorio 40+',
              value: '$238 MXN',
              desc: '14 recetas mexicanas — sí, MEXICANAS, con ingredientes que encuentras en cualquier mercado — diseñadas para reducir la inflamación hormonal.'
            }].map((item, i) => <div key={i} className="bg-[#F9F7F2] p-5 rounded-2xl border border-[#E8E2D5] flex gap-4" data-id="element-144">
                  <img src={item.thumb} alt={item.title} className="w-16 h-20 md:w-20 md:h-24 rounded-lg object-contain shrink-0 bg-white" data-id="element-145" />
                  <div className="flex-1 min-w-0" data-id="element-146">
                    <div className="flex justify-between items-start gap-3 mb-2 flex-wrap" data-id="element-147">
                      <h4 className="font-bold text-dark-text text-sm md:text-base flex-1" data-id="element-148">
                        {item.title}
                      </h4>
                      <span className="bg-mint-green text-forest-green px-2.5 py-1 rounded-full text-xs font-bold whitespace-nowrap" data-id="element-149">
                        {item.value}
                      </span>
                    </div>
                    <p className="text-sm text-dark-text/70 leading-snug" data-id="element-150">
                      {item.desc}
                    </p>
                  </div>
                </div>)}
            </div>

            {/* Total Value Reveal */}
            <div className="mt-10 text-center" data-id="element-151">
              <p className="text-red-600 text-lg font-medium" data-id="element-152">
                Valor Total:{' '}
                <span className="line-through font-bold" data-id="element-153">$1,737 MXN</span>
              </p>
              <p className="text-red-600 text-base mt-1 font-medium" data-id="element-154">
                Precio regular: <span className="line-through" data-id="element-155">$601 MXN</span>
              </p>
              <p className="text-dark-text font-bold text-xl mt-4 mb-2" data-id="element-156">
                Hoy solo pagas:
              </p>
              <div className="text-7xl md:text-8xl font-black text-forest-green tracking-tighter" data-id="element-157">
                ${totalPrice} <span className="text-2xl font-bold" data-id="element-158">MXN</span>
              </div>
              <p className="text-sm text-muted-text mt-2" data-id="element-159">(IVA incluido)</p>
              {(bump1 || bump2) && <p className="text-sm text-forest-green font-semibold mt-2" data-id="element-160">
                  Incluye{' '}
                  {bump1 && bump2 ? 'tus 2 mejoras seleccionadas' : 'tu mejora seleccionada'}
                </p>}
              <div className="inline-block bg-amber-100 text-amber-900 font-bold text-sm px-4 py-2 rounded-full mt-4" data-id="element-161">
                💰 Ahorras ${totalSavings} MXN hoy
              </div>
            </div>

            {/* Order Bumps Section - inside same card */}
            <div className="mt-10 pt-10 border-t-2 border-dashed border-amber-400" data-id="element-162">
              <h3 className="font-serif text-2xl md:text-3xl font-bold mb-2 text-dark-text text-center" data-id="element-163">
                Antes de irte — 2 piezas que la mayoría descubre cuando ya es
                tarde
              </h3>
              <p className="text-center text-muted-text mb-8 text-sm" data-id="element-164">
                Tu señal hormonal ya va a estar funcionando. Esto es para que
                nada la vuelva a apagar.
              </p>

              <div className="space-y-4" data-id="element-165">
                <motion.label animate={bump1 ? {
                scale: 1,
                boxShadow: '0 0 0 0 rgba(20,88,62,0)'
              } : {
                scale: [1, 1.025, 1],
                boxShadow: ['0 0 0 0 rgba(251,191,36,0.45)', '0 0 0 12px rgba(251,191,36,0)', '0 0 0 0 rgba(251,191,36,0)']
              }} transition={bump1 ? {
                duration: 0.2
              } : {
                duration: 1.8,
                repeat: Infinity,
                ease: 'easeInOut'
              }} whileHover={{
                scale: 1.03
              }} whileTap={{
                scale: 0.98
              }} className={`block p-5 rounded-2xl border-2 cursor-pointer transition-colors ${bump1 ? 'border-forest-green bg-mint-green/15 shadow-md' : 'border-amber-400 bg-amber-50/40 hover:border-forest-green'}`} data-id="element-166">
                  <div className="flex items-start gap-4" data-id="element-167">
                    <input type="checkbox" className="mt-1.5 w-5 h-5 accent-forest-green shrink-0" checked={bump1} onChange={e => setBump1(e.target.checked)} data-id="element-168" />
                    <img src="https://cdn.magicpatterns.com/uploads/bJaGwLnfhoKrWfxTekNQDz/ChatGPT-Image-11-may-2026_-18_31_56-_1_.webp" alt="Checklist: Hogar Libre de Fugas Hormonales" className="w-16 h-20 md:w-20 md:h-24 rounded-lg object-contain shrink-0 bg-white" data-id="element-169" />
                    <div className="flex-1 min-w-0" data-id="element-170">
                      <div className="flex flex-wrap items-center gap-2 mb-1" data-id="element-171">
                        <span className="font-bold text-dark-text" data-id="element-172">
                          Checklist: Hogar Libre de Fugas Hormonales
                        </span>
                        <span className="text-forest-green bg-mint-green px-2 py-0.5 rounded text-xs font-bold whitespace-nowrap" data-id="element-173">
                          +$178 MXN
                        </span>
                      </div>
                      <p className="text-sm font-semibold text-forest-green mb-2" data-id="element-174">
                        🔒 Protege tu resultado
                      </p>
                      <p className="text-sm text-dark-text/75 leading-snug" data-id="element-175">
                        No dejes que tu detergente o tu crema facial vuelvan a
                        apagar la señal que acabas de encender. Te doy la lista
                        de los 12 químicos 'impostores de estrógeno' que están
                        en tu baño ahora mismo y cómo sustituirlos por opciones
                        que ya tienes en casa. Protege tu reinicio de
                        interferencias externas.
                      </p>
                      <p className="text-xs text-muted-text mt-2" data-id="element-176">
                        (IVA incluido)
                      </p>
                    </div>
                  </div>
                </motion.label>

                <motion.label animate={bump2 ? {
                scale: 1,
                boxShadow: '0 0 0 0 rgba(20,88,62,0)'
              } : {
                scale: [1, 1.025, 1],
                boxShadow: ['0 0 0 0 rgba(251,191,36,0.45)', '0 0 0 12px rgba(251,191,36,0)', '0 0 0 0 rgba(251,191,36,0)']
              }} transition={bump2 ? {
                duration: 0.2
              } : {
                duration: 1.8,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: 0.9
              }} whileHover={{
                scale: 1.03
              }} whileTap={{
                scale: 0.98
              }} className={`block p-5 rounded-2xl border-2 cursor-pointer transition-colors ${bump2 ? 'border-forest-green bg-mint-green/15 shadow-md' : 'border-amber-400 bg-amber-50/40 hover:border-forest-green'}`} data-id="element-177">
                  <div className="flex items-start gap-4" data-id="element-178">
                    <input type="checkbox" className="mt-1.5 w-5 h-5 accent-forest-green shrink-0" checked={bump2} onChange={e => setBump2(e.target.checked)} data-id="element-179" />
                    <img src="https://cdn.magicpatterns.com/uploads/8BpAKzQkkTYcRmYFBz1xJ7/ChatGPT-Image-11-may-2026_-18_35_03-_1_.webp" alt="Guía: Drenaje Linfático Térmico Facial" className="w-16 h-20 md:w-20 md:h-24 rounded-lg object-contain shrink-0 bg-white" data-id="element-180" />
                    <div className="flex-1 min-w-0" data-id="element-181">
                      <div className="flex flex-wrap items-center gap-2 mb-1" data-id="element-182">
                        <span className="font-bold text-dark-text" data-id="element-183">
                          Guía: Drenaje Linfático Térmico Facial
                        </span>
                        <span className="text-forest-green bg-mint-green px-2 py-0.5 rounded text-xs font-bold whitespace-nowrap" data-id="element-184">
                          +$117 MXN
                        </span>
                      </div>
                      <p className="text-sm font-semibold text-forest-green mb-2" data-id="element-185">
                        ✨ Refleja tu nueva energía
                      </p>
                      <p className="text-sm text-dark-text/75 leading-snug" data-id="element-186">
                        Ahora que tu temperatura está regulada, usa este
                        protocolo de 2 minutos frente al espejo para drenar la
                        hinchazón matutina del rostro. No necesitas cremas
                        caras, solo la técnica de presión térmica para que tu
                        cara refleje la energía que acabas de recuperar.
                      </p>
                      <p className="text-xs text-muted-text mt-2" data-id="element-187">
                        (IVA incluido)
                      </p>
                    </div>
                  </div>
                </motion.label>
              </div>
            </div>
          </div>

          {/* Final CTA in this section - dynamic price */}
          <div className="text-center" data-id="element-188">
            <button onClick={handleCheckout} className="w-full max-w-lg mx-auto bg-forest-green text-white py-6 px-6 rounded-2xl font-black text-xl md:text-2xl flex items-center justify-center gap-3 hover:bg-[#0f4430] transition-transform hover:scale-[1.02] shadow-[0_10px_40px_rgba(20,88,62,0.4)] mb-4" data-id="element-189">
              Obtener El Protocolo Completo — ${totalPrice} MXN{' '}
              <ArrowRight className="w-6 h-6" data-id="element-190" />
            </button>
            <div className="flex items-center justify-center gap-2 text-sm font-medium text-forest-green" data-id="element-191">
              <ShieldCheck className="w-4 h-4" data-id="element-192" /> Garantía de 7 Noches •
              Devolución Total Sin Preguntas
            </div>
          </div>
        </motion.div>
      </section>

      {/* L. Testimonios */}
      <section className="py-16 bg-white px-4" data-id="element-193">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeUp} className="max-w-5xl mx-auto" data-id="element-194">
          <h2 className="font-serif text-3xl md:text-4xl font-bold mb-3 text-center" data-id="element-195">
            Mujeres como tú ya lo hicieron
          </h2>
          <p className="text-center text-muted-text mb-12" data-id="element-196">
            No son celebridades. Son mujeres reales con los mismos síntomas que
            tú.
          </p>

          <div className="grid md:grid-cols-3 gap-6" data-id="element-197">
            {[{
            name: 'Mariana G., 40',
            loc: 'CDMX, México',
            before: '2 años sin dormir',
            after: 'Durmió en la noche 3',
            quote: 'Era escéptica. ¿Un protocolo de ducha? ¿En serio? Pero en la noche 3 dormí hasta que sonó mi alarma por primera vez en 2 años. Me desperté y lloré.',
            avatar: 'https://cdn.magicpatterns.com/uploads/thJezZb7512wcjisxKFs8y/ChatGPT_Image_11_may_2026,_16_07_13.webp'
          }, {
            name: 'Patricia L., 55',
            loc: 'Guadalajara, México',
            before: '$12,000 en suplementos',
            after: 'Al día 5, ligera otra vez',
            quote: 'Me gasté más de $12,000 pesos en suplementos este año. Este protocolo de $359 hizo lo que ninguno pudo. Al día 5 mi esposo me dijo "te ves… más ligera". Supe exactamente a qué se refería.',
            avatar: 'https://cdn.magicpatterns.com/uploads/sSzEXqsMAEDKd6RTkvhdQP/ChatGPT_Image_11_may_2026,_16_28_14.webp'
          }, {
            name: 'Dra. Carmen R., 67',
            loc: 'Monterrey, México',
            before: 'Escéptica total',
            after: 'Funcionó y soy médica',
            quote: 'Soy médica. Confío en la ciencia. Cuando leí sobre el eje HPA y la regulación térmica, supe que esto no era otra cosa de "manifiesta tus hormonas". Es biología real. Y funcionó.',
            avatar: 'https://cdn.magicpatterns.com/uploads/5Y4dkh1ysrw5pryz34KhJZ/dra.webp'
          }].map((t, i) => <div key={i} className="bg-cream p-6 rounded-3xl border border-[#E8E2D5] flex flex-col" data-id="element-198">
                <div className="flex items-center gap-3 mb-4" data-id="element-199">
                  <img src={t.avatar} alt={t.name} className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-md" data-id="element-200" />
                  <div className="flex-1 min-w-0" data-id="element-201">
                    <p className="font-bold text-sm flex items-center gap-1" data-id="element-202">
                      {t.name}
                      <Check className="w-3.5 h-3.5 text-white bg-blue-500 rounded-full p-0.5" data-id="element-203" />
                    </p>
                    <p className="text-xs text-muted-text" data-id="element-204">{t.loc}</p>
                  </div>
                </div>
                <div className="flex text-amber-400 mb-3" data-id="element-205">
                  {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-current" data-id="element-206" />)}
                </div>
                <div className="bg-white rounded-xl p-3 mb-4 text-xs space-y-1 border border-[#E8E2D5]" data-id="element-207">
                  <p data-id="element-208">
                    <span className="font-bold text-red-600" data-id="element-209">Antes:</span>{' '}
                    <span className="text-dark-text/80" data-id="element-210">{t.before}</span>
                  </p>
                  <p data-id="element-211">
                    <span className="font-bold text-forest-green" data-id="element-212">
                      Después:
                    </span>{' '}
                    <span className="text-dark-text/80" data-id="element-213">{t.after}</span>
                  </p>
                </div>
                <p className="text-dark-text/85 leading-relaxed italic text-[0.95rem]" data-id="element-214">
                  "{t.quote}"
                </p>
              </div>)}
          </div>
        </motion.div>
      </section>
      {/* M. Guarantee */}
      <section className="py-16 bg-cream px-4" data-id="element-215">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeUp} className="max-w-3xl mx-auto" data-id="element-216">
          <div className="bg-white p-8 md:p-12 rounded-[2.5rem] border-2 border-forest-green shadow-sm text-center relative" data-id="element-217">
            <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-mint-green rounded-full p-4 border-4 border-white shadow-md" data-id="element-218">
              <ShieldCheck className="w-10 h-10 text-forest-green" data-id="element-219" />
            </div>
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6 mt-6" data-id="element-220">
              La Garantía de "7 Noches"
            </h2>
            <p className="text-lg text-dark-text/85 mb-6 leading-relaxed" data-id="element-221">
              Sigue el protocolo durante 7 noches. Si NO duermes al menos UNA
              noche completa sin que un bochorno te despierte — te devuelvo
              hasta el último peso. Sin preguntas. Sin formularios. Te quedas
              con el protocolo. Si este método no puede darte UNA sola noche de
              sueño completo, no merece tu dinero. Y punto.
            </p>
            <div className="bg-mint-green/30 border border-mint-green rounded-xl p-5" data-id="element-222">
              <p className="text-base font-bold text-forest-green leading-relaxed" data-id="element-223">
                Pruébalo 7 noches. Si no duermes al menos UNA noche completa sin
                que un bochorno te despierte — no pagas nada. Te quedas con el
                protocolo de todas formas.
              </p>
            </div>
          </div>
        </motion.div>
      </section>
      {/* N. FAQ */}
      <section className="py-16 bg-white px-4" data-id="element-224">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeUp} className="max-w-3xl mx-auto" data-id="element-225">
          <h2 className="font-serif text-3xl md:text-4xl font-bold mb-10 text-center" data-id="element-226">
            Preguntas Frecuentes
          </h2>
          <div className="space-y-3" data-id="element-227">
            {[{
            q: '¿Esto es terapia hormonal? ¿Necesito receta?',
            a: 'No. El Método de Cambio Térmico™ no es terapia hormonal ni requiere recetas médicas. Es un protocolo basado en termorregulación circadiana — usa los estímulos térmicos correctos en el momento correcto para reactivar la señal natural de tu eje HPA. No hay pastillas, hormonas ni suplementos involucrados.'
          }, {
            q: '¿En qué se diferencia de todas las demás guías de menopausia?',
            a: 'La diferencia es la SEÑAL. La mayoría de guías te dan otra dieta, otra rutina de ejercicio, u otra lista de suplementos. Este protocolo trabaja en una capa más profunda: reabre las compuertas térmicas para que la señal entre tu cerebro y tus ovarios vuelva a llegar. Por eso funciona cuando todo lo demás falló.'
          }, {
            q: '¿Necesito equipo especial?',
            a: 'No. Solo necesitas tu ducha y un par de minutos al día. No hay equipos costosos, no hay aparatos especiales. El protocolo está diseñado para hacerse desde casa con lo que ya tienes.'
          }, {
            q: '¿Qué pasa si no me funciona?',
            a: 'Tienes la Garantía de 7 Noches. Si sigues el protocolo durante 7 noches y no duermes al menos UNA noche completa sin que un bochorno te despierte, te devolvemos el 100% de tu dinero. Sin preguntas, sin formularios. Te quedas con el protocolo de todas formas.'
          }, {
            q: '¿Es descarga inmediata?',
            a: 'Sí. Apenas completas tu pago, recibes en tu correo todos los materiales en formato PDF digital. Puedes descargarlos y empezar a leerlos esa misma noche. El protocolo empieza cuando tú decidas.'
          }, {
            q: '¿El pago es seguro?',
            a: 'Sí, completamente seguro. Tu pago se procesa a través de Hotmart, la plataforma de productos digitales líder en Latinoamérica con presencia en más de 156 países y millones de transacciones procesadas. Tu información financiera está protegida con encriptación de nivel bancario y cuenta con el respaldo de la Garantía de 7 días de Hotmart: si por cualquier motivo no estás conforme, recibes el 100% de tu dinero de vuelta sin preguntas. Tu pago es 100% seguro.'
          }].map((faq, i) => <div key={i} className="border-b border-[#E8E2D5]" data-id="element-228">
                <button onClick={() => toggleFaq(i)} className="w-full text-left py-5 font-bold flex justify-between items-center hover:text-forest-green transition-colors" data-id="element-229">
                  <span className="pr-4 text-base md:text-lg" data-id="element-230">{faq.q}</span>
                  <ChevronDown className={`w-5 h-5 text-forest-green shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} data-id="element-231" />
                </button>
                <AnimatePresence initial={false} data-id="element-232">
                  {openFaq === i && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3
              }} className="overflow-hidden" data-id="element-233">
                      <p className="pb-5 text-dark-text/75 leading-relaxed" data-id="element-234">
                        {faq.a}
                      </p>
                    </motion.div>}
                </AnimatePresence>
              </div>)}
          </div>
        </motion.div>
      </section>
      {/* O. Una Última Cosa */}
      <section className="py-16 bg-[#F4EFE6] px-4 text-center" data-id="element-235">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={fadeUp} className="max-w-2xl mx-auto" data-id="element-236">
          <h2 className="font-serif text-3xl md:text-4xl font-bold mb-8" data-id="element-237">
            Una Última Cosa
          </h2>

          <div className="space-y-5 text-lg text-dark-text/85 mb-10 leading-relaxed" data-id="element-238">
            <p data-id="element-239">
              Si sigues leyendo, probablemente ya has intentado mucho. Lo
              entiendo. Estás cansada de tener esperanzas.
            </p>
            <p data-id="element-240">
              El Método de Cambio Térmico™ no es esperanza. Es una señal. La
              señal exacta que tu cuerpo ha estado esperando desde que la
              perimenopausia empezó a apagar todo.
            </p>
            <p className="font-serif italic text-forest-green text-xl" data-id="element-241">
              7 días. $359 MXN. Una noche de sueño puede cambiarlo todo.
            </p>
          </div>

          <button className="w-full max-w-md mx-auto bg-forest-green text-white py-5 px-6 rounded-2xl font-bold text-xl flex items-center justify-center gap-2 hover:bg-[#0f4430] transition-transform hover:scale-[1.02] shadow-[0_8px_25px_rgba(20,88,62,0.3)] mb-3" data-id="element-242">
            Obtener El Protocolo — ${totalPrice} MXN{' '}
            <ArrowRight className="w-6 h-6" data-id="element-243" />
          </button>
          <p className="text-xs text-muted-text mb-6" data-id="element-244">(IVA incluido)</p>

          <p className="text-sm font-medium text-forest-green mb-10" data-id="element-245">
            Garantía de 7 Noches • Descarga Inmediata • Pago Seguro
          </p>

          <div className="border-t border-[#E8E2D5] pt-8 space-y-3" data-id="element-246">
            <p className="text-xs text-muted-text leading-relaxed max-w-xl mx-auto" data-id="element-247">
              El Método de Cambio Térmico™ no es un tratamiento médico. Estos
              protocolos están diseñados para bienestar general y apoyo al
              estilo de vida. Los resultados varían. Consulta a tu médico antes
              de comenzar cualquier protocolo de salud nuevo.
            </p>
            <p className="text-xs text-muted-text" data-id="element-248">
              © 2026 El Protocolo de Reinicio Hormonal. Todos los derechos
              reservados.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Social proof — floating purchase notifications */}
      <SocialProofToast data-id="element-249" />
    </div>;
};